/** @jsxImportSource @emotion/react */
import { useState, useRef } from "react";
import { Navigate, useNavigate, useOutletContext } from "react-router-dom";
import {
  Boton,
  Titulo,
  Formulario,
  accessAPI
} from "../../../shared/index.js";
import { Card } from "primereact/card";
import { Sidebar } from "../../../shared";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { ProgressBar } from "primereact/progressbar";
import { Divider } from "primereact/divider";
import { Skeleton } from "primereact/skeleton";

/**
 * Sección "Certificado" del proceso.
 *
 * - Si el proceso ya tiene un certificado emitido, redirige a la subruta
 *   anidada `/proceso/:procesoId/certificado/:certificadoId`, donde se
 *   monta el componente Certificado existente dentro de este layout.
 * - Si todavía no hay certificado, muestra el estado vacío con el botón
 *   "Emitir certificado" y el sidebar de emisión (lógica trasplantada de
 *   la versión anterior de Proceso.js).
 */
export default function ProcesoCertificado() {
  const { proceso, loading } = useOutletContext();
  const navigate = useNavigate();
  const toast = useRef(null);

  const [sidebarEmision, setSidebarEmision] = useState(false);
  const [loaderEmision, setLoaderEmision] = useState(false);
  const [analisisRiesgoEmision, setAnalisisRiesgoEmision] = useState(null);
  const [loaderAnalisisRiesgo, setLoaderAnalisisRiesgo] = useState(false);
  const [actividadEmisionId, setActividadEmisionId] = useState(null);

  if (loading || !proceso) {
    return (
      <div>
        <div
          css={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "1em",
            marginBottom: "1em"
          }}
        >
          <Skeleton width="20rem" height="2rem" />
          <Skeleton width="12rem" height="2.5rem" />
        </div>
        <Card>
          <div
            css={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "1em",
              padding: "2em"
            }}
          >
            <Skeleton shape="circle" size="4em" />
            <Skeleton width="20rem" height="1.2rem" />
          </div>
        </Card>
      </div>
    );
  }

  // Si ya hay certificado emitido, redirigir a la ruta anidada con id.
  if (proceso?.certificado?.length > 0) {
    return (
      <Navigate
        to={`/proceso/${proceso.id}/certificado/${proceso.certificado[0].id}`}
        replace
      />
    );
  }

  function cargarAnalisisRiesgo(actividadId) {
    if (!actividadId) {
      setAnalisisRiesgoEmision(null);
      setActividadEmisionId(null);
      return;
    }
    setActividadEmisionId(actividadId);
    setLoaderAnalisisRiesgo(true);
    accessAPI(
      "GET",
      `admin/actividad/${actividadId}`,
      null,
      (respuesta) => {
        setLoaderAnalisisRiesgo(false);
        setAnalisisRiesgoEmision(
          respuesta.analisisriesgo || { _sinCalcular: true }
        );
      },
      () => {
        setLoaderAnalisisRiesgo(false);
        setAnalisisRiesgoEmision(null);
      }
    );
  }

  function emitirCertificado(camposFormulario) {
    setLoaderEmision(true);
    accessAPI(
      "POST",
      "admin/certificado",
      camposFormulario,
      (respuesta) => {
        setSidebarEmision(false);
        navigate(
          `/proceso/${proceso.id}/certificado/${respuesta.nuevoCertificado.id}`,
          respuesta.erroresEmision?.length
            ? { state: { erroresEmision: respuesta.erroresEmision } }
            : undefined
        );
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al emitir certificado",
          sticky: true
        });
        setLoaderEmision(false);
      }
    );
  }

  const emisionFormConfig = proceso
    ? {
        secciones: [
          {
            campos: [
              {
                nombre: "actividadid",
                tipoitemformularioid: 5,
                etiqueta: "Actividad relacionada a la emisión",
                requerido: true,
                opciones: `admin/actividad/proceso/${proceso.id}?correspondeemision=true&dropdown=true`,
                sinOpcionInicial: true,
                onChange: cargarAnalisisRiesgo
              },
              {
                nombre: "notas",
                tipoitemformularioid: 11,
                etiqueta: "Notas para la emisión",
                requerido: false
              }
            ]
          }
        ],
        botones: [
          {
            etiqueta: "Emitir certificado",
            clases: "verde",
            callback: emitirCertificado,
            confirmacion: "¿Emitir certificado?",
            validar: true
          }
        ]
      }
    : null;

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <Titulo
        urlVolver={`/clientes/${proceso.cliente.id}/procesos`}
        subtitulo={`${proceso.cliente.razonsocial} - ${proceso.nombrefantasia}`}
        botones={
          <Boton verde callback={() => setSidebarEmision(true)}>
            Emitir certificado
          </Boton>
        }
      >
        Proceso {proceso.nombre} - Certificado
      </Titulo>

      <Card>
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "1em",
            padding: "2em",
            color: "var(--grisTextos)"
          }}
        >
          <i className="pi pi-verified" css={{ fontSize: "3em" }} />
          <p css={{ margin: 0 }}>
            Este proceso todavía no tiene un certificado emitido.
          </p>
        </div>
      </Card>

      <Sidebar
        visible={sidebarEmision}
        onHide={() => {
          setSidebarEmision(false);
          setAnalisisRiesgoEmision(null);
          setActividadEmisionId(null);
        }}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Emitir certificado</Titulo>
        {emisionFormConfig && (
          <Formulario config={emisionFormConfig} loader={loaderEmision} />
        )}

        {(loaderAnalisisRiesgo || analisisRiesgoEmision) && (
          <>
            <Divider />
            <div
              css={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "0.5rem"
              }}
            >
              <h4 css={{ margin: 0 }}>Análisis de Riesgo</h4>
              {actividadEmisionId && (
                <Boton
                  texto
                  negro
                  uri={`/proceso/${proceso.id}/actividades/${actividadEmisionId}`}
                >
                  <i className="pi pi-external-link" />
                </Boton>
              )}
            </div>
            {loaderAnalisisRiesgo ? (
              <div
                css={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  padding: "0.75rem",
                  backgroundColor: "var(--surface-100)",
                  borderRadius: "6px"
                }}
              >
                <i
                  className="pi pi-spin pi-spinner"
                  css={{ color: "var(--grisTextos)" }}
                />
                <span
                  css={{ color: "var(--grisTextos)", fontSize: "0.9rem" }}
                >
                  Cargando...
                </span>
              </div>
            ) : analisisRiesgoEmision?.riesgoTotal !== undefined ? (
              <div
                css={{
                  padding: "1rem",
                  backgroundColor:
                    analisisRiesgoEmision.riesgoTotal < 100
                      ? "rgba(76, 175, 80, 0.1)"
                      : "rgba(244, 67, 54, 0.1)",
                  borderRadius: "6px"
                }}
              >
                <div
                  css={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "0.5rem"
                  }}
                >
                  <span css={{ fontWeight: "500", fontSize: "0.9rem" }}>
                    Valor de riesgo:
                  </span>
                  <Tag
                    value={analisisRiesgoEmision.riesgoTotal}
                    severity={
                      analisisRiesgoEmision.riesgoTotal < 100
                        ? "success"
                        : "danger"
                    }
                  />
                </div>
                <ProgressBar
                  value={Math.max(
                    0,
                    Math.min(100, analisisRiesgoEmision.riesgoTotal / 2)
                  )}
                  showValue={false}
                  css={{
                    height: "6px",
                    ".p-progressbar-value": {
                      backgroundColor:
                        analisisRiesgoEmision.riesgoTotal < 100
                          ? "var(--verde)"
                          : "var(--rojo)"
                    }
                  }}
                />
                <p
                  css={{
                    marginTop: "0.5rem",
                    fontSize: "0.8rem",
                    color: "var(--grisTextos)"
                  }}
                >
                  {analisisRiesgoEmision.riesgoTotal < 100
                    ? "Riesgo menor a 100: hay suficientes garantías para emitir."
                    : "Riesgo igual o mayor a 100: el riesgo es demasiado grande para emitir."}
                </p>
              </div>
            ) : (
              <div
                css={{
                  padding: "0.75rem",
                  backgroundColor: "var(--surface-100)",
                  borderRadius: "6px"
                }}
              >
                <p
                  css={{
                    color: "var(--grisTextos)",
                    margin: 0,
                    fontSize: "0.9rem"
                  }}
                >
                  Análisis de riesgo no calculado para esta actividad.
                </p>
              </div>
            )}
          </>
        )}
      </Sidebar>
    </>
  );
}
